import React, { useEffect, useMemo, useState } from 'react';
import { BOAPI } from '../service/UsersService';

const fmtDate = (d) => new Date(d).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const FILTERS = [
  { key: 0, label: 'ממתינים' },
  { key: 1, label: 'טופלו' },
  { key: undefined, label: 'הכל' },
];

// טאב "דיווחים על שיחות" - שיחות בין לקוח לבית עסק שאחד הצדדים דיווח עליהן
// מהאפליקציה (conversation.tsx). ההנהלה רואה רק שיחות שדווחו, לא את כל
// ההודעות (כך גם במדיניות הפרטיות). כל דיווח מציג את תוכן השיחה המלא +
// סימון "טופל". חסימה היא בידי בית העסק עצמו; לקוח מטריד - אפשר להשבית
// את המשתמש בטאב משתמשים
const ConversationReportsTab = () => {
  const [reports, setReports] = useState([]);
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    load(filter);
  }, [filter]);

  const load = async (handled) => {
    try {
      setLoading(true);
      setError('');
      const resp = await BOAPI.fetchConversationReports(handled);
      if (resp?.[0]?.[0]?.code === 0) {
        setReports(resp[1] || []);
        setMessages(resp[2] || []);
      } else {
        setError(resp?.[0]?.[0]?.msg || 'שגיאה בטעינת הדיווחים');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setLoading(false);
    }
  };

  const messagesByConversation = useMemo(() => {
    const map = {};
    messages.forEach((m) => {
      (map[m.conversationId] = map[m.conversationId] || []).push(m);
    });
    return map;
  }, [messages]);

  const toggleHandled = async (report) => {
    setBusyId(report.id);
    setError('');
    try {
      const resp = await BOAPI.setConversationReportHandled(report.id, !report.handled);
      if (resp?.[0]?.[0]?.code === 0) {
        const handled = !!resp[1]?.[0]?.handled;
        // בסינון ממתינים/טופלו - הדיווח עובר לרשימה השנייה
        setReports((prev) => filter === undefined
          ? prev.map((r) => (r.id === report.id ? { ...r, handled } : r))
          : prev.filter((r) => r.id !== report.id));
      } else {
        setError(resp?.[0]?.[0]?.msg || 'העדכון נכשל');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h2 style={s.title}>דיווחים על שיחות</h2>
        <div style={s.filters}>
          {FILTERS.map((f) => (
            <button
              key={String(f.key)}
              style={{ ...s.filterBtn, ...(filter === f.key ? s.filterBtnActive : {}) }}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <p style={s.hint}>
        שיחות בין לקוחות לבתי עסק שאחד הצדדים דיווח עליהן מהאפליקציה. מוצגות כאן רק שיחות שדווחו.
        בית העסק יכול לחסום לקוח בעצמו; לקוח שממשיך להטריד אפשר להשבית בטאב משתמשים.
      </p>

      {error && (
        <div style={s.errorBanner}>❌ {error}
          <button style={s.closeErr} onClick={() => setError('')}>✕</button>
        </div>
      )}

      {loading ? (
        <div style={s.center}>טוען...</div>
      ) : reports.length === 0 ? (
        <div style={s.emptyState}>
          <div style={{ fontSize: 48 }}>🛡️</div>
          <p style={{ color: '#697386', fontWeight: 600 }}>{filter === 0 ? 'אין דיווחים שממתינים לטיפול' : 'אין דיווחים'}</p>
        </div>
      ) : (
        <div style={s.list}>
          {reports.map((r) => {
            const convMessages = messagesByConversation[r.conversationId] || [];
            const open = openId === r.id;
            return (
              <div key={r.id} style={{ ...s.card, ...(r.handled ? s.cardHandled : {}) }}>
                <div style={s.cardHeader}>
                  <div>
                    <div style={s.parties}>
                      🏪 {r.businessName} ↔ 👤 {r.customerName}
                      <span style={s.contact}>{r.customerPhone}</span>
                      {r.blocked && <span style={s.blockedBadge}>הלקוח חסום ע"י העסק</span>}
                    </div>
                    <div style={s.reporter}>
                      דווח ע"י {r.reporterSide === 'business' ? 'בית העסק' : 'הלקוח'}: {r.reporterName}
                      <span style={s.contact}>{r.reporterPhone}</span>
                    </div>
                  </div>
                  <span style={s.date}>{fmtDate(r.iTime)}</span>
                </div>

                <p style={s.reason}>{r.reason || <span style={{ color: '#8792a2' }}>(לא צוינה סיבה)</span>}</p>

                <button style={s.toggleConvBtn} onClick={() => setOpenId(open ? null : r.id)}>
                  {open ? '▲ הסתרת השיחה' : `▼ הצגת השיחה (${convMessages.length} הודעות)`}
                </button>

                {open && (
                  <div style={s.conversation}>
                    {convMessages.map((m) => (
                      <div key={m.id} style={{ ...s.msg, ...(m.senderSide === 'business' ? s.msgBusiness : s.msgCustomer) }}>
                        <div style={s.msgMeta}>
                          {m.senderSide === 'business' ? `🏪 ${m.senderName}` : `👤 ${m.senderName}`} · {fmtDate(m.iTime)}
                        </div>
                        <div style={s.msgBody}>{m.body}</div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={s.cardFooter}>
                  <span style={{ ...s.statusBadge, ...(r.handled ? s.statusActive : s.statusInactive) }}>
                    {r.handled ? `טופל${r.handledAt ? ` · ${fmtDate(r.handledAt)}` : ''}` : 'ממתין לטיפול'}
                  </span>
                  <button
                    style={r.handled ? s.markPendingBtn : s.markHandledBtn}
                    onClick={() => toggleHandled(r)}
                    disabled={busyId === r.id}
                  >
                    {busyId === r.id ? '...' : r.handled ? 'סימון כלא טופל' : 'סימון כטופל'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const s = {
  container:    { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 12 },
  title:        { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  filters:      { display: 'flex', gap: 6 },
  filterBtn:    { padding: '6px 14px', background: '#fff', color: '#4f566b', border: '1px solid #e3e8ee', borderRadius: 16, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  filterBtnActive: { background: '#1f6feb', color: '#fff', borderColor: '#1f6feb' },
  hint:         { color: '#8792a2', fontSize: 12.5, marginBottom: 18, lineHeight: 1.6 },
  center:       { padding: 40, textAlign: 'center', color: '#999' },
  errorBanner:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  closeErr:     { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18 },
  emptyState:   { textAlign: 'center', padding: '60px 0' },

  list:         { display: 'flex', flexDirection: 'column', gap: 12 },
  card:         { border: '1px solid #e3e8ee', borderRadius: 12, padding: 16, background: '#fff' },
  cardHandled:  { background: '#fafbfc', opacity: 0.8 },
  cardHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 8 },
  parties:      { fontWeight: 700, color: '#1a1f36', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  reporter:     { color: '#4f566b', fontSize: 12.5, marginTop: 4 },
  contact:      { color: '#8792a2', fontSize: 12.5, fontWeight: 400 },
  blockedBadge: { background: '#fff1f1', color: '#d93838', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700 },
  date:         { color: '#8792a2', fontSize: 12 },
  reason:       { color: '#4f566b', fontSize: 13.5, lineHeight: 1.6, margin: '0 0 10px 0', whiteSpace: 'pre-wrap' },
  toggleConvBtn:{ background: 'transparent', border: 'none', color: '#1f6feb', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0, marginBottom: 10 },
  conversation: { display: 'flex', flexDirection: 'column', gap: 8, background: '#f6f8fa', borderRadius: 10, padding: 12, marginBottom: 12, maxHeight: 420, overflowY: 'auto' },
  msg:          { borderRadius: 10, padding: '8px 12px', maxWidth: '80%' },
  msgCustomer:  { alignSelf: 'flex-start', background: '#fff', border: '1px solid #e3e8ee' },
  msgBusiness:  { alignSelf: 'flex-end', background: '#e8f0fe', border: '1px solid #d2e3fc' },
  msgMeta:      { color: '#8792a2', fontSize: 11, marginBottom: 3 },
  msgBody:      { color: '#1a1f36', fontSize: 13.5, lineHeight: 1.5, whiteSpace: 'pre-wrap' },
  cardFooter:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  statusBadge:  { display: 'inline-block', borderRadius: 12, padding: '3px 10px', fontSize: 11, fontWeight: 700 },
  statusActive: { background: '#e8f5e9', color: '#2e7d32' },
  statusInactive:{ background: '#fff8e1', color: '#a06d00' },
  markHandledBtn:{ padding: '6px 12px', background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  markPendingBtn:{ padding: '6px 12px', background: '#fff1f1', color: '#d93838', border: '1px solid #fbc4c4', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
};

export default ConversationReportsTab;
